import { useEffect } from "react";

/**
 * Lock document body scroll while `active` is true.
 * Restores the previous overflow + paddingRight on unmount or deactivation.
 * Adds a compensating padding-right equal to the scrollbar width so the page
 * doesn't shift when the scrollbar disappears.
 *
 * Tracks an active-locks counter so multiple stacked modals don't fight over
 * the body style — only the first lock writes, only the last unlock restores.
 */
let lockCount = 0;
let saved = null;

function applyLock() {
  if (lockCount > 0) { lockCount += 1; return; }
  if (typeof document === "undefined") return;
  const body = document.body;
  const html = document.documentElement;
  const scrollbarWidth = window.innerWidth - html.clientWidth;
  saved = {
    overflow: body.style.overflow,
    paddingRight: body.style.paddingRight,
  };
  body.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    const prev = parseInt(window.getComputedStyle(body).paddingRight, 10) || 0;
    body.style.paddingRight = `${prev + scrollbarWidth}px`;
  }
  lockCount = 1;
}

function releaseLock() {
  if (lockCount <= 0) return;
  lockCount -= 1;
  if (lockCount > 0) return;
  if (typeof document === "undefined") return;
  const body = document.body;
  body.style.overflow = saved?.overflow ?? "";
  body.style.paddingRight = saved?.paddingRight ?? "";
  saved = null;
}

export default function useBodyScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    applyLock();
    return () => releaseLock();
  }, [active]);
}
